import React from 'react';
import { motion } from 'framer-motion';
export type ShapeType = 'rectangle' | 'square' | 'triangle' | 'parallelogram' | 'trapezoid';
interface GeometricShapeProps {
  shape: ShapeType;
  base: number;
  height: number;
  sideLength?: number; // For parallelogram slant
  topBase?: number; // For trapezoid top edge width
  unit?: string;
  label?: string;
  showGrid?: boolean;
  showDimensions?: boolean;
  hideHeight?: boolean; // Hide height dimension (when it's the answer)
  mode?: 'stimulus' | 'support';
}
export function GeometricShape({
  shape,
  base,
  height,
  sideLength,
  topBase,
  unit = 'cm',
  label,
  showGrid = true,
  showDimensions = true,
  hideHeight = false,
  mode = 'stimulus'
}: GeometricShapeProps) {
  const cellSize = 24;
  const padding = 60;
  // Calculate SVG dimensions
  const svgWidth = base * cellSize + padding * 2;
  const svgHeight = height * cellSize + padding * 2;
  // Origin at bottom-left
  const originX = padding;
  const originY = svgHeight - padding;
  // Convert grid coordinates to SVG
  const toSvgX = (x: number) => originX + x * cellSize;
  const toSvgY = (y: number) => originY - y * cellSize;
  // Calculate shape path
  const getShapePath = (): string => {
    const x0 = toSvgX(0);
    const y0 = toSvgY(0);
    const x1 = toSvgX(base);
    const y1 = toSvgY(0);
    const x2 = toSvgX(base);
    const y2 = toSvgY(height);
    const x3 = toSvgX(0);
    const y3 = toSvgY(height);
    // F-GEOM-SQUARE (mei 2026): seeds use shape="square" but the path
    // builder previously fell through every branch and returned an empty
    // string — the SVG drew nothing. A square is just a rectangle with
    // base === height, so we render it via the same path.
    if (shape === 'rectangle' || shape === 'square') {
      return `M ${x0} ${y0} L ${x1} ${y1} L ${x2} ${y2} L ${x3} ${y3} Z`;
    }
    if (shape === 'triangle') {
      // Right triangle: base along bottom, height vertical on left
      return `M ${x0} ${y0} L ${x1} ${y1} L ${x0} ${y3} Z`;
    }
    if (shape === 'parallelogram') {
      // Parallelogram: slant offset in grid units (not pixels)
      const slantCells = sideLength || base * 0.3;
      const x2Slant = toSvgX(base + slantCells);
      const x3Slant = toSvgX(slantCells);
      return `M ${x0} ${y0} L ${x1} ${y1} L ${x2Slant} ${y3} L ${x3Slant} ${y3} Z`;
    }
    if (shape === 'trapezoid') {
      // Trapezoid: configurable top edge width, centered
      const topWidth = topBase ?? base * 0.6;
      const offset = (base - topWidth) / 2;
      const x2Top = toSvgX(base - offset);
      const x3Top = toSvgX(offset);
      return `M ${x0} ${y0} L ${x1} ${y1} L ${x2Top} ${y3} L ${x3Top} ${y3} Z`;
    }
    return '';
  };
  // Grid cells (only for rectangle background reference)
  const renderGrid = () => {
    if (!showGrid) return null;
    const cells = [];
    for (let row = 0; row < height; row++) {
      for (let col = 0; col < base; col++) {
        cells.push(<rect key={`${row}-${col}`} x={toSvgX(col)} y={toSvgY(row + 1)} width={cellSize} height={cellSize} fill="none" stroke="rgb(203, 213, 225)" strokeWidth="1" opacity={0.4} data-id="element-12141" />);
      }
    }
    return <g className="grid-lines" data-id="element-12142">{cells}</g>;
  };
  // Height line (perpendicular to base)
  const renderHeightLine = () => {
    if (!showDimensions || hideHeight || shape === 'rectangle' || shape === 'square') return null;
    const x0 = toSvgX(0);
    const y0 = toSvgY(0);
    const y1 = toSvgY(height);
    return <g className="height-indicator" data-id="element-12143">
        {/* Dashed line */}
        <line x1={x0} y1={y0} x2={x0} y2={y1} stroke="rgb(239, 68, 68)" strokeWidth="2" strokeDasharray="4 4" data-id="element-12144" />
        {/* Perpendicular markers */}
        <line x1={x0 - 6} y1={y0} x2={x0 + 6} y2={y0} stroke="rgb(239, 68, 68)" strokeWidth="2" data-id="element-12145" />
        <line x1={x0 - 6} y1={y1} x2={x0 + 6} y2={y1} stroke="rgb(239, 68, 68)" strokeWidth="2" data-id="element-12146" />
      </g>;
  };
  // Area calculation
  const calculateArea = (): number => {
    if (shape === 'rectangle' || shape === 'square' || shape === 'parallelogram') {
      return base * height;
    }
    if (shape === 'triangle') {
      return base * height / 2;
    }
    if (shape === 'trapezoid') {
      const topWidth = topBase ?? base * 0.6;
      return (base + topWidth) / 2 * height;
    }
    return 0;
  };
  return <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 w-full overflow-hidden" data-id="element-12147">
      {label && <div className="text-sm font-medium text-slate-700 mb-3 text-center" data-id="element-12148">
          {label}
        </div>}

      <div className="flex justify-center w-full" data-id="element-12149">
        <motion.svg viewBox={`0 0 ${svgWidth} ${svgHeight}`} style={{
        width: '100%',
        height: 'auto',
        maxWidth: svgWidth
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-12150">
          {/* Grid (background reference) */}
          {renderGrid()}

          {/* Shape */}
          <motion.path d={getShapePath()} fill="rgb(191, 219, 254)" stroke="rgb(59, 130, 246)" strokeWidth="3" initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-12151" />

          {/* Height indicator line */}
          {renderHeightLine()}

          {/* Base dimension (bottom) */}
          {showDimensions && <text x={toSvgX(base / 2)} y={toSvgY(0) + 25} textAnchor="middle" className="text-sm font-bold fill-slate-700" data-id="element-12152">
              {base} {unit}
            </text>}

          {/* Height dimension (left) */}
          {showDimensions && !hideHeight && <text x={toSvgX(0) - 20} y={toSvgY(height / 2)} textAnchor="end" dominantBaseline="middle" className="text-sm font-bold fill-red-600" data-id="element-12153">
              {height} {unit}
            </text>}

          {/* Side length for parallelogram */}
          {showDimensions && shape === 'parallelogram' && sideLength && <text x={toSvgX(base) + 15} y={toSvgY(height / 2)} textAnchor="start" dominantBaseline="middle" className="text-xs font-medium fill-slate-500" data-id="element-12154">
              {sideLength} {unit}
            </text>}
        </motion.svg>
      </div>

      {/* Area calculation (support mode only) */}
      {mode === 'support' && <div className="mt-4 text-center text-sm text-slate-600" data-id="element-12155">
          <p data-id="element-12156">
            {shape === 'triangle' && <>
                Oppervlakte = (basis × hoogte) ÷ 2 = ({base} × {height}) ÷ 2 ={' '}
                {calculateArea()} {unit}²
              </>}
            {shape === 'parallelogram' && <>
                Oppervlakte = basis × hoogte = {base} × {height} ={' '}
                {calculateArea()} {unit}²
              </>}
            {shape === 'rectangle' && <>
                Oppervlakte = basis × hoogte = {base} × {height} ={' '}
                {calculateArea()} {unit}²
              </>}
            {shape === 'trapezoid' && <>
                Oppervlakte = (basis + top) ÷ 2 × hoogte = ({base} +{' '}
                {topBase ?? base * 0.6}) ÷ 2 × {height} = {calculateArea()}{' '}
                {unit}²
              </>}
          </p>
        </div>}
    </div>;
}